import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Bell, BellOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export function PushNotificationToggle() {
  const { api } = useAuth();
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    checkSubscription();
  }, []);

  const checkSubscription = async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      setIsSupported(false);
      return;
    }
    setIsSupported(true);

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      setIsSubscribed(!!subscription);
    } catch (e) {
      console.error('Push check failed:', e);
    }
  };

  const subscribe = async () => {
    setLoading(true);
    try {
      // Get VAPID public key from server
      const { data } = await api.get('/api/push/vapid-public-key');
      const publicKey = data.public_key;

      // Convert VAPID key to Uint8Array
      const urlBase64ToUint8Array = (base64String) => {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);
        for (let i = 0; i < rawData.length; ++i) {
          outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
      };

      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey)
      });

      // Send subscription to server
      const subJson = subscription.toJSON();
      await api.post('/api/push/subscribe', {
        endpoint: subJson.endpoint,
        keys: subJson.keys
      });

      setIsSubscribed(true);
    } catch (error) {
      console.error('Push subscription failed:', error);
      if (Notification.permission === 'denied') {
        toast.warning('Notifications are blocked. Please enable them in your browser settings.');
      }
    } finally {
      setLoading(false);
    }
  };

  const unsubscribe = async () => {
    setLoading(true);
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        const subJson = subscription.toJSON();
        await api.post('/api/push/unsubscribe', {
          endpoint: subJson.endpoint,
          keys: subJson.keys
        });
        await subscription.unsubscribe();
      }
      
      setIsSubscribed(false);
    } catch (error) {
      console.error('Unsubscribe failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const sendTest = async () => {
    try {
      await api.post('/api/push/send-test');
    } catch (e) {
      console.error('Test push failed:', e);
    }
  };

  if (!isSupported) return null;

  return (
    <div className="flex items-center gap-2">
      {loading ? (
        <Loader2 className="w-4 h-4 animate-spin text-[#00E5FF]" />
      ) : isSubscribed ? (
        <>
          <Button
            variant="ghost"
            size="sm"
            onClick={unsubscribe}
            className="text-[#00E676] hover:text-[#00E676]/80 text-xs px-2"
            data-testid="push-unsubscribe-btn"
          >
            <Bell className="w-4 h-4 mr-1" />
            Notifications On
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={sendTest}
            className="text-[#5C7599] hover:text-white text-xs px-2"
            data-testid="push-test-btn"
          >
            Test
          </Button>
        </>
      ) : (
        <Button
          variant="ghost"
          size="sm"
          onClick={subscribe}
          className="text-[#A0B4CC] hover:text-[#00E5FF] text-xs px-2"
          data-testid="push-subscribe-btn"
        >
          <BellOff className="w-4 h-4 mr-1" />
          Enable Notifications
        </Button>
      )}
    </div>
  );
}
